import React, { useState } from "react";
import { Flex } from "styles/utility-components";

import { withAppLayout } from "components/app_layout/AppLayout";
import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import { FilterableDocuments } from "components/documents/FilterableDocuments";
import {
  DEFAULT_DOCUMENT_FILTERS,
  DocumentFilters,
  parseDocumentsUrlState,
} from "components/documents/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import Button from "components/ui/button/Button";
import { SaveViewModal } from "components/views/SaveViewModal";
import { DOCUMENT_VIEWS, useVariableValue } from "lib/featureFlags";
import { ViewConfigurationFilterSettingsRelevanceScoreEnum } from "lib/generated/app-api";
import { CreateDocumentViewRequest_View_FilterSettings_SignalScoreEnum_1 } from "lib/generated/app-service-gql/graphql";
import { useCreateSavedDocumentView } from "lib/hooks/api/documents/useCreateDocumentSavedView";
import { useURLState } from "lib/hooks/useURLState";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { TrackingProvider } from "lib/tracking";

import css from "./DocumentsPage.module.scss";

// Helper function to convert between the two enum types
function convertSignalScoreEnum(
  scores: ViewConfigurationFilterSettingsRelevanceScoreEnum[],
): CreateDocumentViewRequest_View_FilterSettings_SignalScoreEnum_1[] {
  return scores.map(
    (score) => score as CreateDocumentViewRequest_View_FilterSettings_SignalScoreEnum_1,
  );
}

function convertDocumentFiltersToSavedViewFilters(filters: DocumentFilters) {
  // Create a copy of filters without the sort field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { sort, ...filtersWithoutSort } = filters;
  return {
    ...filtersWithoutSort,
    signalScore: convertSignalScoreEnum(filters.signalScore),
  };
}

export function DocumentsPage() {
  const { authorised: hasDocuments } = useCheckSubscription("DOCUMENTS");
  const isDocumentViewsEnabled = useVariableValue(DOCUMENT_VIEWS, false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [filters = DEFAULT_DOCUMENT_FILTERS] = useURLState<DocumentFilters>(
    "documentFilters",
    DEFAULT_DOCUMENT_FILTERS,
    parseDocumentsUrlState,
  );

  const { mutate: createSavedDocumentView } = useCreateSavedDocumentView();

  return (
    <DetailsPage>
      <SaveViewModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        // TODO: Use alert frequency to create a notification entry
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        saveView={({ viewName, viewDescription, alertFrequency }) => {
          createSavedDocumentView({
            request: {
              name: viewName,
              description: viewDescription,
              view: {
                sortOrder: {
                  field: filters.sort.field,
                  order: filters.sort.order,
                },
                tableSettings: {
                  columns: [
                    { field: "title" },
                    { field: "buyers" },
                    { field: "type" },
                    { field: "publishedAt" },
                  ],
                },
                filterSettings: {
                  ...convertDocumentFiltersToSavedViewFilters(filters),
                },
              },
            },
          });
          setIsCreateModalOpen(false);
        }}
      />
      <DetailsHeader>
        <Flex justifyContent="space-between" alignItems="center">
          <h1 className={css.title}>Documents</h1>
          {isDocumentViewsEnabled && (
            <Button
              onClick={() => {
                setIsCreateModalOpen(true);
              }}
            >
              Save
            </Button>
          )}
        </Flex>
      </DetailsHeader>
      <TrackingProvider data={{ "Page source": "Document search" }}>
        {!hasDocuments && <PaywallBanner featureType="DOCUMENTS" />}
        <DetailsContent className={css.content}>
          <FilterableDocuments defaultFilters={{ ...DEFAULT_DOCUMENT_FILTERS }} showBanner={true} />
        </DetailsContent>
      </TrackingProvider>
    </DetailsPage>
  );
}

export default withAppLayout(DocumentsPage, {
  pageName: "Documents",
});
